import React, { useState, useEffect } from "react";
import {
  Layout,
  Button,
  Table,
  Space,
  Modal,
  Input,
  Form,
  Popconfirm,
  message,
  Card,
  Typography,
  Upload,
} from "antd";

import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  DragOutlined,
  EyeOutlined,
} from "@ant-design/icons";

import {
  createPdf,
  getPdf,
  updatePdf,
  deletePdf,
  updatePdfImage,
  updatePdfPriority,
} from "../services/pdfUploadService";

import ImageGridSorter from "./ImageGridSorter";

const { Content } = Layout;
const { Title } = Typography;

function PdfUpload() {
  const [data, setData] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [form] = Form.useForm();

  const [pdfFile, setPdfFile] = useState(null);
  const [fileList, setFileList] = useState([]);

  const [imageBase64, setImageBase64] = useState("");
  const [imageFileList, setImageFileList] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 5;

  const [isOrderView, setIsOrderView] = useState(false);
  const [orderedData, setOrderedData] = useState([]);

  // ================= FETCH =================
  const fetchPdf = async (page = 1) => {
    try {
      const res = await getPdf(page, pageSize);
      setData(res.data.data);
      setTotalItems(res.data.pagination.totalItems);
      setCurrentPage(page);
    } catch {
      message.error("Failed to fetch");
    }
  };

  useEffect(() => {
    fetchPdf(currentPage);
  }, [currentPage]);

  // ================= ADD =================
  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setPdfFile(null);
    setFileList([]);
    setImageFileList([]);
    setImageBase64("");
    setOpen(true);
  };

  // ================= SUBMIT =================
  const handleSubmit = async (values) => {
    try {
      if (editingItem) {
        await updatePdf(editingItem.id, {
          title: values.title,
        });

        message.success("Updated Successfully");
      } else {
        if (!pdfFile || !imageBase64)
          return message.error("Upload PDF & Image");

        const reader = new FileReader();
        reader.readAsDataURL(pdfFile);

        reader.onload = async () => {
          await createPdf({
            title: values.title,
            image: imageBase64,
            uploadpdf: reader.result,
          });

          message.success("Added Successfully");
          fetchPdf(currentPage);
        };
      }

      setOpen(false);
      fetchPdf(currentPage);
    } catch {
      message.error("Operation Failed");
    }
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    await deletePdf(id);
    message.success("Deleted");
    fetchPdf(currentPage);
  };

  // ================= EDIT =================
  const handleEdit = (record) => {
    setEditingItem(record);
    setOpen(true);

    form.setFieldsValue({
      title: record.title,
    });
  };

  // ================= VIEW PDF =================
  const handleViewPdf = (url) => {
    window.open(url, "_blank");
  };

  // ================= IMAGE UPDATE =================
  const handleImageChange = (file, id) => {
    const reader = new FileReader();

    reader.onload = async () => {
      await updatePdfImage(id, {
        image: reader.result,
      });

      message.success("Image updated");
      fetchPdf(currentPage);
    };

    reader.readAsDataURL(file);
  };

  // ================= REORDER =================
  const handleSaveOrder = async () => {
    try {
      const source = orderedData.length ? orderedData : data;

      const reorderedItems = source.map((item, index) => ({
        id: item.id,
        priority: index,
      }));

      await updatePdfPriority({ reorderedItems });

      message.success("Order updated");

      const updated = source.map((item) =>
        data.find((d) => d.id === item.id)
      );

      setData(updated);
    } catch {
      message.error("Reorder Failed");
    }
  };

  // ================= TABLE =================
  const columns = [
    {
      title: "Image",
      render: (_, record) => (
        <Space direction="vertical">
         <img
  src={record.image}
  alt="pdf"
  style={{
    width: 60,
    height: 60,
    objectFit: "cover",
    borderRadius: 6,
  }}
/>

          <Upload
            showUploadList={false}
            beforeUpload={(file) => {
              handleImageChange(file, record.id);
              return false;
            }}
          >
            <Button size="small" icon={<UploadOutlined />}>
              Update
            </Button>
          </Upload>
        </Space>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
    },
    {
      title: "Action",
      render: (_, record) => (
        <Space direction="vertical">
          {/* VIEW PDF */}
          <Button
            icon={<EyeOutlined />}
            onClick={() => handleViewPdf(record.uploadpdf)}
          >
            View PDF
          </Button>

          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>

          <Popconfirm
            title="Delete?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout>
      <Content style={{ padding: 20 }}>
        <Card title={<Title level={4}>PDF Upload</Title>}>

          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add PDF
          </Button>

          <Button
            style={{ marginLeft: 10 }}
            icon={<DragOutlined />}
            onClick={async () => {
              if (!isOrderView) {
                const res = await getPdf(1, 1000);
                const allData = res?.data?.data || [];

                setData(allData);

                setOrderedData(
                  allData.map((i) => ({
                    id: i.id,
                    image: i.image,
                    title: i.title,
                  }))
                );
              } else {
                fetchPdf(currentPage);
              }

              setIsOrderView(!isOrderView);
            }}
          >
            {isOrderView ? "Back to List" : "Reorder"}
          </Button>

          {isOrderView && (
            <Button
              type="primary"
              style={{ marginLeft: 10 }}
              onClick={handleSaveOrder}
            >
              Save Order
            </Button>
          )}

          <br /><br />

          {isOrderView ? (
            <ImageGridSorter
              data={data.map((i) => ({
                id: i.id,
                image: i.image,
                title: i.title,
              }))}
              onChange={(newOrder) => {
                setOrderedData(newOrder);

                const updated = newOrder.map((item) =>
                  data.find((d) => d.id === item.id)
                );

                setData(updated);
              }}
            />
          ) : (
            <Table
              columns={columns}
              dataSource={data}
              rowKey="id"
              pagination={{
                current: currentPage,
                pageSize,
                total: totalItems,
                onChange: (page) => fetchPdf(page),
              }}
            />
          )}
        </Card>
      </Content>

      {/* MODAL */}
      <Modal
        title={editingItem ? "Edit PDF" : "Add PDF"}
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
      >
        <Form layout="vertical" form={form} onFinish={handleSubmit}>

          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          {!editingItem && (
            <>
              {/* IMAGE */}
              <Form.Item label="Upload Image" required>
                <Upload
                  listType="picture-card"
                  fileList={imageFileList}
                  beforeUpload={(file) => {
                    const reader = new FileReader();
                    reader.onload = () => setImageBase64(reader.result);
                    reader.readAsDataURL(file);
                    setImageFileList([file]);
                    return false;
                  }}
                  onRemove={() => {
                    setImageFileList([]);
                    setImageBase64("");
                  }}
                >
                  {imageFileList.length === 0 && "+ Upload"}
                </Upload>
              </Form.Item>

              {/* PDF */}
              <Form.Item label="Upload PDF">
                <Upload
                  fileList={fileList}
                  beforeUpload={(file) => {
                    setPdfFile(file);
                    setFileList([file]);
                    return false;
                  }}
                  onRemove={() => {
                    setPdfFile(null);
                    setFileList([]);
                  }}
                >
                  <Button icon={<UploadOutlined />}>
                    Upload PDF
                  </Button>
                </Upload>
              </Form.Item>
            </>
          )}

          <Button type="primary" htmlType="submit" block>
            {editingItem ? "Update" : "Add"}
          </Button>

        </Form>
      </Modal>
    </Layout>
  );
}

export default PdfUpload;